import { useMutation, useQueryClient } from '@tanstack/react-query';

import { adminApi } from '@/api';
import { useToast } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';

/**
 * Verify or reject one kind of student document from the admin student
 * record -- the same calls as the Verify documents queue, so a decision taken
 * on either screen shows on the other.
 *
 * `kind` is 'resume', 'certification' or 'diploma'; `noun` is how the toasts
 * name it ("CV", "certificate").
 */
export function useDocumentDecision({ kind, noun, student }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['admin-student', student.id] });
    void queryClient.invalidateQueries({ queryKey: ['admin-document-verifications'] });
    void queryClient.invalidateQueries({ queryKey: ['admin-document-verification-counts'] });
    void queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
  }

  const approve = useMutation({
    mutationFn: (document) => adminApi.approveDocument(kind, document.id),
    onSuccess: () => {
      toast.success(`${student.full_name}'s ${noun} is verified`);
      invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, `Could not verify the ${noun}`)),
  });

  const reject = useMutation({
    mutationFn: ({ document, reason }) => adminApi.rejectDocument(kind, document.id, reason),
    onSuccess: () => {
      toast.success(`The ${noun} was rejected. The student has been told why.`);
      invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, `Could not reject the ${noun}`)),
  });

  return { approve, reject };
}
